import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import EditableList from '../editableList/EditableList';
import { Row, Tag, Checkbox } from 'antd';
import { Input } from '../index';
import moment from 'moment';
import PropTypes from 'prop-types';
import './SideProject.css';
import { SEGMENTIO } from '../../../constants/segmentio';

function SideProject (props) {
  const handleValidation = (item) => {
    const getDate = (start_end) => {
      const month = item[`${start_end}_month`];
      const year = item[`${start_end}_year`];
      if (month && year) return moment(`${year}-${month}`, 'YYYY-MMMM');
      return null;
    };
    const start_date = getDate('start');
    const end_date = getDate('end');
    if (!item.project_name) { item.error('project_name', 'Please type the project name'); }
    if (!item.details) item.error('details', 'Please type the project details');
    if (!item.start_year) item.error('start_date', 'Please select the start month');
    if (item.notStillWorking && !end_date) item.error('end_date', 'Please select the end month');
    if (start_date && end_date && item.notStillWorking) {
      if (moment(start_date.format('YYYY-MM-01')).isAfter(end_date.format('YYYY-MM-01'))) {
        item.error('start_date', 'Should be before the end date');
        item.error('end_date', 'Should be after the start date');
      }
    }
  };

  return (
    <EditableList
      {...props}
      onChange={props.onChange}
      textAddButton="Add project"
      addEvent={SEGMENTIO.PROFILE_ADD_PROJECT_EVENT_CLICKED}
      cancelEvent={SEGMENTIO.PROFILE_CANCEL_PROJECT_EVENT_CLICKED}
      saveEvent={SEGMENTIO.PROFILE_SAVE_PROJECT_EVENT_CLICKED}
      dataSource={props.projects || []}
      allowAdd
      addTitle="Add a new side project"
      editTitle="Update side project"
      validation={handleValidation}
      renderForm={(item) => (
        <SideProjectForm {...item} skills={props.skills || []} />
      )}
      renderItem={(item, i) => (
        <SideProjectItem {...item} skills={props.skills || []} key={i} />
      )}
    />
  );
}

const SideProjectForm = React.memo((props) => {
  const handleDateChange = (start_end) => (value) => {
    const month = `${start_end}_month`;
    const year = `${start_end}_year`;
    if (value) {
      if (moment(value.format('YYYY-MMMM'), 'YYYY-MMMM').isBefore(new Date(), 'day')) {
        props.set({
          [month]: value.format('MMMM'),
          [year]: value.format('YYYY')
        });
      }
    } else {
      props.set({
        [month]: null,
        [year]: null
      });
    }
  };

  const disabledDate = value => {
    return value && moment(value.format('YYYY-MMMM'), 'YYYY-MMMM').isAfter(new Date(), 'day');
  };

  const handleReadDate = (start_end) => () => {
    const month = props[`${start_end}_month`];
    const year = props[`${start_end}_year`];
    if (month && year) return moment(`${year}-${month}`, 'YYYY-MMMM');
  };

  const handleNotStillWorkingChange = (notStillWorking) => {
    if (!notStillWorking) props.set({ end_year: 0, end_month: '' });
  };

  useEffect(() => {
    if (props.notStillWorking !== true && props.notStillWorking !== false) {
      props.set({ notStillWorking: +props.end_year > 0 });
    }
  }, []);

  const stripHtml = (html) => {
    if (typeof html !== 'string') return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html.replace(/<\/p>/g, '\r\n</p>');
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <Row gutter={[12, 0]}>
      <Input
        span={24}
        {...props.bind('project_name', { defaultValue: '' })}
        label="Project name"
        placeholder="Project name"
      />
      <Input.Month
        span={12}
        className="select-month"
        format="MMMM YYYY"
        disabledDate={disabledDate}
        {...props.bind('start_date', {
          onChange: handleDateChange('start'),
          onReadValue: handleReadDate('start')
        })}
        label="Start date"
      />
      {props.notStillWorking
        ? (
          <Input.Month
            span={12}
            className="select-month"
            format="MMMM YYYY"
            disabledDate={disabledDate}
            {...props.bind('end_date', {
              onChange: handleDateChange('end'),
              onReadValue: handleReadDate('end')
            })}
            label={
              <Checkbox
                style={{ height: 0 }}
                {...props.bind('notStillWorking', {
                  onChange: handleNotStillWorkingChange,
                  bindProp: 'checked'
                })}
              >
              End date
              </Checkbox>
            }
          />
        )
        : (
          <Input
            span={12}
            disabled={true}
            value="Still working here"
            label={
              <Checkbox
                style={{ height: 0 }}
                {...props.bind('notStillWorking', {
                  onChange: handleNotStillWorkingChange,
                  bindProp: 'checked'
                })}
              >
              End date
              </Checkbox>
            }
          />
        )}
      <Input.TextArea
        span={24}
        className="side-project-details"
        label="Details"
        placeholder="Details"
        limit={1200}
        rows={7}
        {...props.bind('details', { onReadValue: stripHtml })}
      />
      <Input span={24} label="Link" placeholder="Link" {...props.bind('url')} />
    </Row>
  );
});

const SideProjectItem = React.memo((props) => {
  const skillsIds = `${props.project_skills}`
    .split(',')
    .map((id) => parseInt(id));
  const skills = (props.skills || []).filter((skill) =>
    skillsIds.includes(skill.id)
  );
  return (
    <div className="side-project-item">
      <h2>{props.project_name}</h2>
      <small><a href={props.url} target="_blank" rel="noopener noreferrer">{props.url}</a></small>
      <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.details) }}/>
      <div className="project-skills-list">
        {skills.map((skill) => (
          <Tag key={skill.id}>{skill.skill_name}</Tag>
        ))}
      </div>
    </div>
  );
});

SideProject.propTypes = {
  onChange: PropTypes.func,
  projects: PropTypes.array,
  skills: PropTypes.array
};

SideProjectForm.propTypes = {
  set: PropTypes.func,
  bind: PropTypes.func,
  notStillWorking: PropTypes.bool,
  end_year: PropTypes.number
};

SideProjectItem.propTypes = {
  project_name: PropTypes.string,
  url: PropTypes.string,
  details: PropTypes.string,
  skills: PropTypes.array,
  project_skills: PropTypes.string
};

export default SideProject;
